import React, { useState, useEffect } from 'react';
import StorageManager from '../utils/StorageManager';
import './StatisticsScreen.css';

/**
 * StatisticsScreen - Tela de Estatísticas
 * Exibe estatísticas gerais do jogador e permite reset de dados
 * 
 * @param {function} onBackToMenu - Callback para voltar ao menu
 */
const StatisticsScreen = ({ onBackToMenu }) => {
  const [stats, setStats] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    // Carregar estatísticas ao montar componente
    loadStatistics();
  }, []);

  /**
   * Carrega estatísticas do StorageManager
   */
  const loadStatistics = () => {
    const globalStats = StorageManager.getGlobalStatistics();
    setStats(globalStats);
  };

  /**
   * Formata tempo em horas:minutos
   */
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  /**
   * Formata data ISO para legível
   */
  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '-';
    }
  };

  /**
   * Handler para resetar dados
   */
  const handleResetData = () => {
    const success = StorageManager.resetAllData();
    if (success) {
      loadStatistics();
      setShowResetConfirm(false);
    }
  };

  // Loading state
  if (!stats) {
    return (
      <div className="statistics-screen loading">
        <div className="loading-spinner"></div>
        <p>Carregando estatísticas...</p>
      </div>
    );
  }

  return (
    <div className="statistics-screen">
      {/* Header */}
      <div className="stats-header">
        <button className="btn-back-stats" onClick={onBackToMenu}>
          ← Voltar
        </button>
        <h1 className="stats-title">📊 Estatísticas</h1>
        <div className="stats-subtitle">Seu Desempenho</div>
      </div>

      {/* Grid de Estatísticas */}
      <div className="stats-grid">
        {/* High Score Global */}
        <div className="stat-card highlight">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <div className="stat-label">High Score</div>
            <div className="stat-value big">
              {stats.highScore.toLocaleString()}
            </div>
            {stats.highScoreDate && (
              <div className="stat-detail">
                {formatDate(stats.highScoreDate)}
              </div>
            )}
          </div>
        </div>

        {/* Jogos Jogados */}
        <div className="stat-card">
          <div className="stat-icon">🎮</div>
          <div className="stat-content">
            <div className="stat-label">Jogos Jogados</div>
            <div className="stat-value">
              {stats.gamesPlayed.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Vitórias */}
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Vitórias</div>
            <div className="stat-value">
              {stats.gamesWon.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Taxa de Vitória */}
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-label">Taxa de Vitória</div>
            <div className="stat-value">
              {stats.winRate.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Pontuação Média */}
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <div className="stat-label">Pontuação Média</div>
            <div className="stat-value">
              {stats.averageScore.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Pares Totais */}
        <div className="stat-card">
          <div className="stat-icon">♠️</div>
          <div className="stat-content">
            <div className="stat-label">Pares Encontrados</div>
            <div className="stat-value">
              {stats.totalPairsFound.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Tempo Total Jogado */}
        <div className="stat-card wide">
          <div className="stat-icon">⏱️</div>
          <div className="stat-content">
            <div className="stat-label">Tempo Total de Jogo</div>
            <div className="stat-value">
              {formatTime(stats.totalTimePlayedSeconds)}
            </div>
          </div>
        </div>
      </div>

      {/* Mensagem se não houver dados */}
      {stats.gamesPlayed === 0 && (
        <div className="no-data-message">
          <span className="no-data-icon">🎯</span>
          <p>Você ainda não jogou nenhuma partida.</p>
          <p>Comece a jogar para ver suas estatísticas!</p>
        </div>
      )}

      {/* Botão de Reset */}
      {stats.gamesPlayed > 0 && (
        <div className="stats-footer">
          {!showResetConfirm ? (
            <button 
              className="btn-reset-warning" 
              onClick={() => setShowResetConfirm(true)}
            >
              🗑️ Resetar Todos os Dados
            </button>
          ) : (
            <div className="reset-confirm">
              <p className="reset-warning-text">
                ⚠️ Isso apagará TODAS as suas estatísticas e recordes!
              </p>
              <div className="reset-actions">
                <button 
                  className="btn-confirm-reset" 
                  onClick={handleResetData}
                >
                  Sim, Resetar
                </button>
                <button 
                  className="btn-cancel-reset" 
                  onClick={() => setShowResetConfirm(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatisticsScreen;
